import { useState } from 'react';
import { useNavigate, useSearchParams, useLocation, Navigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogIn, UserPlus, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useRole } from '@/hooks/useRole';
import { toast } from 'sonner';
import { notifyDealerRegistered } from '@/lib/notifications';
import logo from '@/assets/logo.svg';
import Seo from "@/components/Seo";
import { safeInternalReturnPath } from '@/lib/authReturn';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { signIn, signUp, user } = useAuth();
  const { isRep, isAdmin, isAccountManager, isLoading: roleLoading } = useRole();
  const [isSignUp, setIsSignUp] = useState(searchParams.get('signup') === 'true');
  const queryReturnPath = safeInternalReturnPath(searchParams.get('redirect'));
  const stateReturnPath = safeInternalReturnPath((location.state as { from?: string } | null)?.from);
  const returnPath = queryReturnPath ?? stateReturnPath;
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  if (user && !roleLoading) {
    // Return the user to the page they originally tried to open.
    if (returnPath) {
      return <Navigate to={returnPath} replace />;
    }
    // Reps (without admin/AM elevation) land on the Rep portal.
    if (isRep && !isAdmin && !isAccountManager) {
      return <Navigate to="/rep" replace />;
    }
    // Account Managers (without admin elevation) land on the AM portal.
    if (isAccountManager && !isAdmin) {
      return <Navigate to="/am" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (isSignUp) {
      if (!fullName.trim()) {
        setError('Please enter your full name');
        setLoading(false);
        return;
      }
      if (!company.trim()) {
        setError('Please enter your company name');
        setLoading(false);
        return;
      }
      if (!phone.trim()) {
        setError('Please enter your phone number');
        setLoading(false);
        return;
      }
      const { error } = await signUp(email, password, fullName, {
        company: company.trim(),
        phone: phone.trim(),
        returnPath: returnPath ?? undefined,
      });
      if (error) {
        setError(error.message);
      } else {
        // Profile is auto-created by the on_auth_user_created trigger using
        // full_name/company/phone from raw_user_meta_data — no follow-up UPDATE needed.
        toast.success('Account created! Check your email to confirm. Once verified, your account will be reviewed for approval.');
        notifyDealerRegistered(fullName, company.trim(), email);
        setIsSignUp(false);
      }
    } else {
      const { error } = await signIn(email, password);
      if (error) {
        setError(error.message);
      } else {
        navigate(returnPath ?? '/dashboard');
      }
    }
    setLoading(false);
  };

  const handleResendConfirmation = async () => {
    if (!email) {
      toast.error('Enter your email first');
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-user-actions', {
        body: { action: 'resend_confirmation', email },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      toast.success('Confirmation email sent. Check your inbox (and spam folder).');
    } catch (e) {
      toast.error((e as Error).message || 'Could not resend confirmation');
    } finally {
      setLoading(false);
    }
  };

  const showResend = !isSignUp && /confirm/i.test(error);

  return (
    <>
      <Seo title="Dealer Sign In — Amerikooler Quick Ship" description="Sign in to the Amerikooler Quick Ship dealer portal to configure walk-ins, view dealer pricing, and manage quotes and purchase orders." path="/login" />
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="mb-8 text-center">
          <img src={logo} alt="Amerikooler" className="mx-auto mb-5 h-14 w-auto" />
          <h1 className="text-2xl font-bold text-foreground">Quick Ship Dealer Portal</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {isSignUp
              ? 'Request access to dealer pricing and quoting.'
              : 'Dealer pricing, freight, and quotes — in one place.'}
          </p>
        </div>

        <div className="ios-card p-6">
          <div className="flex items-center gap-3 mb-5">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center rounded-lg border border-border/60 bg-background p-2 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              aria-label="Go back to home"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <h2 className="text-base font-semibold text-foreground">
              {isSignUp ? 'Request Dealer Access' : 'Sign In'}
            </h2>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignUp && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input id="fullName" type="text" placeholder="John Doe" value={fullName} onChange={(e) => setFullName(e.target.value)} required className="rounded-xl h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="company">Company Name *</Label>
                  <Input id="company" type="text" placeholder="ABC Refrigeration Inc." value={company} onChange={(e) => setCompany(e.target.value)} required className="rounded-xl h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input id="phone" type="tel" placeholder="(555) 123-4567" value={phone} onChange={(e) => setPhone(e.target.value)} required className="rounded-xl h-11" />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" placeholder="dealer@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-xl h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="pr-10 rounded-xl h-11" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {isSignUp && (
              <div className="rounded-xl bg-accent border border-border/60 px-3 py-2.5 text-xs text-muted-foreground">
                Approved dealer access only. We'll review your request and email you once your account is activated.
              </div>
            )}
            {error && (
              <div className="rounded-xl bg-destructive/8 border border-destructive/15 px-3 py-2.5 text-sm text-destructive space-y-2">
                <div>{error}</div>
                {showResend && (
                  <button
                    type="button"
                    onClick={handleResendConfirmation}
                    disabled={loading}
                    className="text-xs font-semibold text-primary hover:underline"
                  >
                    Resend confirmation email
                  </button>
                )}
              </div>
            )}
            <Button type="submit" className="w-full gap-2 rounded-xl h-11" disabled={loading}>
              {isSignUp ? (
                <>
                  <UserPlus className="h-4 w-4" />
                  {loading ? 'Submitting Request…' : 'Request Dealer Access'}
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4" />
                  {loading ? 'Signing In…' : 'Sign In'}
                </>
              )}
            </Button>
          </form>
          <div className="mt-5 flex flex-col items-center gap-2">
            <button type="button" onClick={() => { setIsSignUp(!isSignUp); setError(''); }} className="text-sm text-primary font-medium hover:underline transition-colors">
              {isSignUp ? 'Already a dealer? Sign in' : 'New dealer? Request access'}
            </button>
            {!isSignUp && (
              <button type="button" onClick={() => navigate('/forgot-password')} className="text-sm text-muted-foreground hover:text-primary hover:underline">
                Forgot your password?
              </button>
            )}
          </div>
          <div className="mt-4 flex justify-center gap-3 text-[11px] text-muted-foreground">
            <button type="button" onClick={() => navigate('/terms')} className="hover:text-primary hover:underline">Terms of Service</button>
            <span>·</span>
            <button type="button" onClick={() => navigate('/privacy')} className="hover:text-primary hover:underline">Privacy Policy</button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Login;
